import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { gql } from '../graphql/generated/gql';
import { updateQueryList } from '../utils/graphql/updateQueryList';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const POST_QUERY = gql(/** @complexity 8 */ `
  query UsePostFavorite($id: ID!) {
    post(id: $id) {
      ...PostSummaryFields
    }
  }
`);

const FAVORITE_POSTS_QUERY = gql(/** @complexity 18 */ `
  query favoritePosts {
    currentSession {
      user {
        id
        favoritePosts {
          ...PostSummaryFields
        }
      }
    }
  }
`);

const POST_FAVORITE_ADDED_SUBSCRIPTION = gql(/** @complexity 32 */ `
  subscription postFavoriteAdded {
    post: postFavoriteAdded {
      ...PostSummaryFields
    }
  }
`);

const POST_FAVORITE_REMOVED_SUBSCRIPTION = gql(/** @complexity 32 */ `
  subscription postFavoriteRemoved {
    post: postFavoriteRemoved {
      ...PostSummaryFields
    }
  }
`);

const FAVORITE_POST_MUTATION = gql(/** @complexity 16 */ `
  mutation favoritePost($id: ID!, $add: Boolean) {
    favoritePost(id: $id, add: $add) {
      ...PostSummaryFields
    }
  }
`);

export default function useFavoritePosts() {
  const { data, subscribeToMore } = useQuery(FAVORITE_POSTS_QUERY);

  useEffect(() => {
    const unsubscribes = [
      POST_FAVORITE_ADDED_SUBSCRIPTION,
      POST_FAVORITE_REMOVED_SUBSCRIPTION,
    ].map((query) =>
      subscribeToMore({
        document: query,
        updateQuery: updateSubscribedList(
          'currentSession.user.favoritePosts',
          'post',
          query === POST_FAVORITE_ADDED_SUBSCRIPTION,
        ),
      }),
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [subscribeToMore]);

  return data?.currentSession?.user?.favoritePosts ?? [];
}

export const useFavoritePost = (postId: string) => {
  const isFavorite = useFavoritePosts().some((fp) => fp.id === postId);

  const { data } = useQuery(POST_QUERY, {
    // The post is only used for optimistic updates
    fetchPolicy: 'cache-only',
    variables: { id: postId },
  });

  const [toggleFavorite] = useMutation(FAVORITE_POST_MUTATION, {
    variables: { id: postId, add: !isFavorite },
    update: updateQueryList(
      { query: FAVORITE_POSTS_QUERY },
      'currentSession.user.favoritePosts',
      'favoritePost',
      !isFavorite,
    ),
    optimisticResponse: data?.post
      ? {
          __typename: 'RootMutationType',
          favoritePost: { ...data.post, __typename: 'Post' },
        }
      : undefined,
  });

  return { isFavorite, toggleFavorite };
};
